import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LookupSearchField from './tablemaint/LookupSearchField';
import LookupResultCard from '../pages/order-management/components/LookupResultCard';
import quoteService from '../services/quote.service';
import type { BillToLookupRow } from '../pages/order-management/fixtures';

/**
 * Reusable enriched Bill To picker: search cms.billtos (name / recno) and store the
 * billto recno, showing the name. Built on the shared <LookupSearchField> + enriched
 * <LookupResultCard> (address + MAS90 / Terms / AR / Credit Hold), the same look as the
 * quote Bill To typeahead. Lookup-only (new bill-tos are created elsewhere). Used by the
 * EDI Invoicing Setup drawer today; drop it in anywhere a record needs a Bill To FK.
 */
export interface BillToValue {
  recno: number; // 0 = unset
  name: string;
  /** Address of the picked lookup row, for the info line under the field.
   *  Absent/null when the value was seeded from a stored record (recno + name
   *  only) — the footer then shows just the #recno, as before. */
  info?: { address1?: string; address2?: string; city?: string; state?: string; zip?: string } | null;
}

const EMPTY: BillToValue = { recno: 0, name: '', info: null };

const addressLine = (info: BillToValue['info']): string => {
  if (!info) return '';
  return [
    info.address1, info.address2, info.city,
    [info.state, info.zip].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
};

interface Props {
  value: BillToValue;
  onChange: (v: BillToValue) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  /** Orange-highlight the input — the stored reference is deleted or no longer
   *  exists (same styling the plain selects use for an unknown value). */
  invalid?: boolean;
}

export const BillToLookupField: React.FC<Props> = ({ value, onChange, label = 'Bill To', disabled, required, invalid }) => (
  <div>
    <LookupSearchField<BillToLookupRow>
      invalid={invalid}
      label={label}
      required={required}
      displayValue={value.name}
      onType={(text) => onChange({ recno: 0, name: text, info: null })}
      onSelect={(r) => onChange({ recno: r.billto, name: r.billtoname, info: r })}
      search={(q) => quoteService.lookupBillTo(q)}
      renderItem={(r) => (
        <LookupResultCard
          name={r.billtoname}
          code={r.billto}
          address1={r.address1}
          address2={r.address2}
          city={r.city}
          state={r.state}
          zip={r.zip}
          details={[
            { label: 'SAGE', value: r.mas90code },
            { label: 'Terms', value: r.creditTerms },
            { label: 'AR', value: r.arBalance },
            { label: 'Credit Hold', value: r.creditHold === 'Y' ? 'Yes' : '', danger: true },
          ]}
        />
      )}
      itemValue={(r) => String(r.billto)}
      itemLabel={(r) => r.billtoname}
      disabled={disabled}
      placeholder="Search Bill Tos by name or #…"
    />
    <div className="mt-1.5 flex items-center gap-3 text-xs">
      {value.recno > 0 ? (
        <span className="inline-flex items-center gap-1 min-w-0 text-green-700 dark:text-green-400">
          #{value.recno}
          {addressLine(value.info) && (
            <span className="ml-1 text-muted truncate">{addressLine(value.info)}</span>
          )}
          {!disabled && (
            <button type="button" onClick={() => onChange(EMPTY)} title="Clear"
              className="ml-1 text-gray-400 hover:text-red-500">
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </span>
      ) : (
        <span className="text-muted">No Bill To selected</span>
      )}
    </div>
  </div>
);

export default BillToLookupField;
